<script setup lang="ts">
import dayjs from 'dayjs';
import { Edit2, Trash2, Users } from 'lucide-vue-next';
import { computed } from 'vue';

import AzureIcon from '@/assets/azure.svg';
import { Button } from '@/components/ui/button';
import type { UserGroup } from '@/services/user_groups';

interface Props {
    group: UserGroup;
    disabled?: boolean;
}

interface Emits {
    (e: 'edit', group: UserGroup): void;
    (e: 'delete', group: UserGroup): void;
    (e: 'manage-members', group: UserGroup): void;
    (e: 'view-members', group: UserGroup): void;
}

const props = defineProps<Props>();
defineEmits<Emits>();

const isAzureGroup = computed(() => props.group.azure_group_id !== null);

const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
};
</script>

<template>
    <div
        class="p-6 bg-card border rounded-lg shadow-sm hover:shadow-md transition-shadow"
    >
        <div class="flex justify-between items-start">
            <div class="flex-1">
                <h3 class="font-bold text-xl mb-2 flex items-center gap-2">
                    <img
                        v-if="isAzureGroup"
                        :src="AzureIcon"
                        class="w-5 h-5"
                        alt="Azure"
                    />
                    {{ group.name }}
                </h3>
                <p v-if="group.description" class="text-muted-foreground mb-3">
                    {{ group.description }}
                </p>
            </div>

            <div class="flex items-center space-x-2 ml-4">
                <Button
                    v-if="isAzureGroup"
                    variant="secondary"
                    size="sm"
                    class="flex items-center gap-2"
                    @click="$emit('view-members', group)"
                    :disabled="disabled"
                    title="View Members"
                >
                    <Users class="w-4 h-4" />
                    <p class="font-normal">View Members</p>
                </Button>
                <Button
                    v-else
                    variant="secondary"
                    size="sm"
                    class="flex items-center gap-2"
                    @click="$emit('manage-members', group)"
                    :disabled="disabled"
                    title="Add or remove Members"
                >
                    <Users class="w-4 h-4" />
                    <p class="font-normal">Manage Members</p>
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    @click="$emit('edit', group)"
                    :disabled="disabled"
                    :class="{ invisible: isAzureGroup }"
                    title="Edit Group"
                >
                    <Edit2 class="w-4 h-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    @click="$emit('delete', group)"
                    :disabled="disabled"
                    class="text-destructive hover:text-destructive"
                    title="Delete Group"
                >
                    <Trash2 class="w-4 h-4" />
                </Button>
            </div>
        </div>

        <div class="flex items-center mt-4 text-sm text-muted-foreground">
            <div v-if="!isAzureGroup" class="flex items-center">
                <Users class="w-4 h-4 mr-1" />
                <span
                    >{{ group.member_count }}
                    {{ group.member_count === 1 ? 'member' : 'members' }}</span
                >
            </div>
            <span class="ml-auto"
                >Created {{ formatDate(group.created_at) }}</span
            >
        </div>
    </div>
</template>
